<template>
  <div>
    <h1>Earns Methoden</h1>
    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>
    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>
    <h1>Pumpe (überschlägig)</h1>

    <v-expansion-panels accordion class="mt-5">
      <!--
        Genauigkeitsüberblick — bewusst das erste Panel. Reihenfolge und Bewertung folgen der
        Entscheidungshierarchie DECLARED > MEASURED > COMPUTED > DEFAULT
        (methods/docs/pump_roadmap.md §1); die Messwerte stammen aus
        methods/docs/pump_calculation_review.md §3–§4.
      -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Genauigkeit der Methoden im Überblick</div>
        </v-expansion-panel-title>
        <!-- eager: Inhalt soll auch ungeöffnet im DOM stehen (Seitensuche, Druck) -->
        <v-expansion-panel-text eager>
          <section class="precision">
            <p>
              Die überschlägigen Methoden sind bewusst so gebaut, dass sie mit dem auskommen, was
              vor Ort ablesbar ist — ein Typenschildfoto. Sie sind damit nicht automatisch
              ungenau: die beste von ihnen ist <b>exakt per Konstruktion</b>. Zwei Methoden sind
              dagegen als <b>veraltet</b> gekennzeichnet und für neue Maßnahmen gesperrt.
            </p>

            <table>
              <thead>
                <tr>
                  <th>Methode</th>
                  <th>Genauigkeit</th>
                  <th>Worauf es ankommt</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Nassläufer: Leistungsschild (P1)</td>
                  <td><b>Sehr hoch</b> — exakt per Konstruktion</td>
                  <td>
                    Bei Nassläufern ist der Typenschildwert bereits die <b>elektrische
                    Leistungsaufnahme</b> — Motor und Pumpe sitzen in einem Gehäuse. Es wird also
                    nichts modelliert, sondern nur mit der Laufzeit multipliziert.
                    <b>DIN V 18599-5 Gl. (63)</b> lässt den Typenschildwert im Bestand
                    ausdrücklich zu. Wichtig ist nur, die richtige <b>Drehzahlstufe</b>
                    abzulesen.
                  </td>
                </tr>
                <tr>
                  <td>Trockenläufer: Regelung</td>
                  <td><b>Gut</b> — abhängig vom Messwert</td>
                  <td>
                    Der Regelungsfaktor stammt aus <b>DIN V 18599-5 Tabelle 28</b>. Er beschreibt
                    das Verhältnis <em>geregelt zu ungeregelt</em> — die eingetragene Leistung
                    muss deshalb die Aufnahme der <b>ungeregelten</b> Pumpe sein. Wird eine
                    bereits geregelte Pumpe gemessen und eingetragen, zählt die Einsparung
                    doppelt. Ist die mittlere Auslastung β bekannt, lohnt sich die Eingabe: über
                    den plausiblen Bereich verändert sie das Ergebnis um bis zu <b>Faktor 2,9</b>.
                  </td>
                </tr>
                <tr>
                  <td>Trockenläufer: Effizienzklasse <b>(veraltet)</b></td>
                  <td><b>Obergrenze</b> — 3–4× zu hoch</td>
                  <td>
                    Für neue Maßnahmen gesperrt. Rechnet den Motor für <b>jede Betriebsstunde
                    mit 100 % des Typenschilds</b>. Da Pumpen fast immer überdimensioniert sind,
                    liegt das Ergebnis auf derselben Pumpe <b>3,2- bis 4,2-mal</b> über der
                    detaillierten Rechnung. Als Abschätzung einer Obergrenze brauchbar, als
                    Verbrauchswert nicht.
                  </td>
                </tr>
                <tr>
                  <td>Nassläufer: DN-Tabelle <b>(veraltet)</b></td>
                  <td><b>Eingeschränkt</b> — nur bis DN 65</td>
                  <td>
                    Für neue Maßnahmen gesperrt. Das Verfahren ist in Ordnung, die hinterlegten
                    Referenzdaten sind es oberhalb <b>DN 65</b> nicht: der Verbrauch
                    <em>sinkt</em> dort mit steigender Nennweite (DN 80 → DN 100 → DN 125), was
                    physikalisch nicht sein kann. Die Werte bis DN 65 sind durch Felddaten
                    bestätigt. Für größere Nennweiten bitte das <b>Leistungsschild</b> verwenden.
                  </td>
                </tr>
              </tbody>
            </table>

            <p>
              <b>Wann die detaillierte Methode „Pumpe“ besser ist:</b> sobald ein
              <b>Betriebspunkt (Q, H)</b> oder gemessene Leistungswerte vorliegen. Mit dem
              Wirkungsgrad im Bestpunkt aus dem Datenblatt trifft sie eine reale Pumpe auf
              <b>+0,3 %</b> genau.
            </p>

            <p class="callout">
              <b>Nutzen bei Pumpen-Maßnahmen.</b> Die <em>Differenz</em> des Nutzens zwischen
              Bestand und Planung ist bei Pumpen <b>nicht belastbar</b> und sollte nicht als
              „eingesparte Nutzenergie“ gelesen werden: Bestand und Planung werden mit
              unterschiedlichen Wirkungsgrad-Annahmen bewertet, sodass ein Austausch gleicher
              Förderleistung rechnerisch eine Änderung des Nutzens ausweist. In den
              überschlägigen Methoden ohne Betriebspunkt ist der Effekt am größten — beim
              Tausch einer alten gegen eine moderne Umwälzpumpe gemessene <b>−76 %</b>.
              Aussagekräftig ist die <b>Stromeinsparung</b>. <b>Kosten, CO₂ und Primärenergie
              sind nicht betroffen</b> — sie werden aus dem Stromverbrauch gebildet.
            </p>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Schritte vor der Berechnung</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ol type="1">
            <li>
              <b>Einsparpotential und Wirtschaftlichkeit mit EARNS-Potentialanalyse
                abschätzen anhand der Typenschilddaten</b>
              <p>Erste Annahmen hierbei:</p>
              <ul>
                <li>
                  Die Bestands-Pumpe läuft im Auslegungspunkt gemäß Typenschild.
                </li>
              </ul>
            </li>
            <li>
              <b>Bedarf überprüfen und mit aktuellem Betriebspunkt vergleichen</b>
              <p>
                Häufig ist die Bestands-Pumpe größer dimensioniert als erforderlich.
                Ziel ist es, die neue Pumpe so klein wie erforderlich auszulegen.
                Hierzu lohnt es sich, mindestens folgende Punkte zu prüfen:
              </p>
              <ul>
                <li>Ist die Pumpe überdimensioniert?</li>
                <li>Läuft die Pumpe länger als eigentlich erforderlich?</li>
                <li>Wurden Gebäudeteile stillgelegt, ohne den Volumenstrom anzupassen?</li>
                <li>Kann durch hydraulischen Abgleich die benötigte Menge reduziert werden?</li>
              </ul>
            </li>
            <li>
              <b>Auslegungs-Betriebspunkt für die neue Pumpe definieren</b>
            </li>
            <li>
              <b>Systemlösung auswählen und finale Einsparhöhe berechnen</b>
            </li>
          </ol>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Eingabeparameter in überschlägige Pumpe</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <table>
            <caption>Pflichtparameter der Methode</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Berechnungsmethode</td>
                <td>
                  Trockenläufer: <b>Regelung</b> oder <b>Effizienzklasse</b>.
                  Nassläufer: <b>Leistungsschild (P1)</b> oder <b>DN-Tabelle</b>.
                </td>
              </tr>
              <tr>
                <td>Rechenmethode</td>
                <td>
                  Die <b>neue Rechenmethode</b> rechnet seit dem 09/2026 nach DIN V 18599 und ist
                  nicht mehr auswählbar — sie gilt für jede neue Maßnahme. Der Regelungsfaktor für
                  <em>elektrisch-geregelt</em> stammt aus
                  <em>DIN V 18599-5:2018-09 Tabelle 28</em> und beträgt <b>0,592</b>
                  (Δp variabel, bei mittlerer Auslastung β = 0,457).<br>
                  <b>Alte Rechenmethode</b> — der bisherige Hauswert <b>0,55</b>. Bereits
                  gespeicherte Maßnahmen behalten sie automatisch, damit erstellte Berichte
                  reproduzierbar bleiben; das wird in der Maßnahme angezeigt.<br>
                  Die Faktoren für <em>ungeregelt</em>, <em>zweistufig</em> und
                  <em>dreistufig</em> sind in beiden Methoden identisch: Tabelle 28 kennt keine
                  stufengeschalteten Pumpen, für sie gibt es also keinen Normwert.
                </td>
              </tr>
              <tr>
                <td>Anzahl</td>
                <td>Anzahl der Pumpen des gleichen Typs und der gleichen Betriebsweise.</td>
              </tr>
              <tr>
                <td>Nennleistung</td>
                <td>
                  Bedeutung je nach Methode:
                  <b>Regelung</b> = gemessene / geschätzte elektrische Leistungsaufnahme;
                  <b>Effizienzklasse (Trockenläufer)</b> = Wellenleistung P2 vom Motor-Typenschild;
                  <b>Nassläufer-Leistungsschild</b> = elektrische Aufnahme P1.
                  (Bei der Methode <b>DN-Tabelle</b> nicht erforderlich.)
                </td>
              </tr>
              <tr>
                <td>Wartungszustand</td>
                <td>
                  Fließt in die Schätzung des Gesamtwirkungsgrades ein, mit dem der Nutzen
                  (hydraulische Arbeit) abgeschätzt wird, wenn kein Betriebspunkt vorliegt.
                </td>
              </tr>
              <tr>
                <td>Norm-Betriebsstunden</td>
                <td>
                  Optional: Aktivieren, um Normwerte zu verwenden. Bei Aktivierung wird
                  die Auswahl der Norm-Betriebsstunden eingeblendet und die manuelle
                  Eingabe der Betriebsstunden gesperrt.<br>
                  Die Werte stammen aus <em>DIN V 18599-7:2018-09 Tabelle D.8</em>
                  (bedarfsgesteuert 1 300 · intermittierend 2 200 · saisonal 5 100 h/a) sowie
                  Tabelle D.4 Betriebsart 4 (ganzjährig 8 760 h/a). Der saisonale Wert wurde am
                  24.08.2026 von zuvor 3 600 h/a auf den Normwert 5 100 h/a korrigiert; bereits
                  gespeicherte Maßnahmen sind davon nicht betroffen, weil die Stundenzahl beim
                  Auswählen in das Feld <em>Betriebsstunden</em> übernommen und dort gespeichert
                  wird.<br>
                  Zu beachten: Tabelle D.8 beschreibt <em>Kaltwasserkreise</em> und bezeichnet die
                  Werte selbst als beispielhaft für ein Bürogebäude. Für Heizungsumwälzpumpen
                  sind sie ein plausibler Startwert, kein Normergebnis.
                </td>
              </tr>
              <tr>
                <td>Betriebsstunden</td>
                <td>
                  Jährliche Betriebsstunden in h/a (nur bei deaktivierten Norm-Betriebsstunden).
                </td>
              </tr>
              <tr>
                <td>Pumpenmanagement</td>
                <td>
                  Ein Wärmeerzeuger mit integriertem Pumpenmanagement schaltet die
                  Umwälzpumpe ab, sobald kein Wärmebedarf besteht — die Pumpe läuft dadurch
                  deutlich weniger Stunden als die Anlage in Betrieb ist. Nach
                  <em>DIN V 18599-5:2018-09 §6.3.2.2, Gl. (56)</em> wird die Betriebszeit
                  mit dem Faktor <code>f<sub>d,PM</sub></code> multipliziert:
                  ohne Pumpenmanagement 1,00 · außentemperaturgeführt 0,75 ·
                  raumtemperaturgeführt 0,45.
                  Voreingestellt ist „ohne“, damit bestehende Berechnungen unverändert bleiben.
                </td>
              </tr>
            </tbody>
          </table>

          <h4>Mittlere Auslastung β (nur neue Rechenmethode)</h4>
          <p>
            β ist die mittlere Belastung der Verteilung nach
            <em>DIN V 18599-5:2018-09 §6.3.2</em>: mittlere abgegebene Leistung geteilt durch die
            Auslegungsleistung. Der Wert entscheidet darüber, wie viel eine Drehzahlregelung
            überhaupt einsparen <em>kann</em> — denn eingespart wird nur der Förderdruck, der im
            Teillastbetrieb nicht gebraucht wird.
          </p>
          <table>
            <caption>
              Stromverbrauch einer Δp-variabel geregelten Pumpe, bezogen auf dieselbe
              ungeregelte Pumpe (DIN V 18599-5 Tabelle 28)
            </caption>
            <thead>
              <tr>
                <th>β</th>
                <th>Faktor</th>
                <th>Einsparung</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>0,20</td><td>0,350</td><td>65 %</td></tr>
              <tr><td>0,40</td><td>0,541</td><td>46 %</td></tr>
              <tr><td>0,457 <em>(Standard)</em></td><td>0,592</td><td>41 %</td></tr>
              <tr><td>0,60</td><td>0,711</td><td>29 %</td></tr>
              <tr><td>0,80</td><td>0,863</td><td>14 %</td></tr>
              <tr><td>1,00</td><td>1,000</td><td><b>0 %</b></td></tr>
            </tbody>
          </table>
          <p>
            Bei β = 1 läuft die Pumpe dauerhaft im Auslegungspunkt — es gibt keinen Überschuss,
            den eine Regelung wegnehmen könnte, und die Einsparung ist null. Umgekehrt lohnt sich
            eine Regelung umso mehr, je überdimensionierter die Pumpe ist. Genau deshalb ist ein
            fester Einsparfaktor für alle Pumpen irreführend.
          </p>
          <p>
            <b>Das Feld ist optional.</b> Bleibt es leer, wird β = 0,457 angesetzt — der
            Betriebspunkt des zertifizierten Moduls, also exakt das bisherige Verhalten. Nur wenn
            die Auslastung tatsächlich bekannt ist, sollte ein eigener Wert eingetragen werden.
          </p>

          <p>
            <strong>Warum das die größte einfache Stellschraube ist.</strong>
            Raumtemperaturgeführtes Pumpenmanagement senkt den Stromverbrauch der Pumpe auf
            <strong>45 %</strong> — mehr als jede Regelungsart am Motor erreicht. Es ist am
            Kesselregler ablesbar und erfordert keine Messung. Da der Faktor die <em>Laufzeit</em>
            verkürzt, sinken Verbrauch und Nutzen im gleichen Verhältnis; der Wirkungsgrad der
            Pumpe bleibt unberührt.
          </p>

          <table>
            <caption>Zusätzliche Parameter je Berechnungsmethode</caption>
            <thead>
              <tr>
                <th>Methode</th>
                <th>Zusätzliche Eingaben</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Regelung (Trockenläufer)</td>
                <td>
                  Regelungsart der Pumpe. Zusätzlich — nur bei der neuen Rechenmethode —
                  optional die <b>mittlere Auslastung β</b>, siehe unten.
                </td>
              </tr>
              <tr>
                <td>Effizienzklasse (Trockenläufer)</td>
                <td>Baujahr sowie VSD / FU.</td>
              </tr>
              <tr>
                <td>Leistungsschild (Nassläufer)</td>
                <td>
                  Elektrische Leistungsaufnahme P1 bei der eingestellten Drehzahlstufe
                  (Nennleistung-Feld).
                </td>
              </tr>
              <tr>
                <td>DN-Tabelle (Nassläufer)</td>
                <td>Nennweite DN und Regelung (ungeregelt / elektrisch / EC-Motor).</td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <h3>Variante „Leistungsschild (P1)“ — Nassläufer</h3>
          <p>
            Der einfachste und zugleich genaueste der überschlägigen Wege:
            <b>E = P1 · Betriebsstunden · Anzahl</b>. Bei Nassläufern sitzen Motor und Pumpe
            in einem Gehäuse, der Typenschildwert ist also bereits die elektrische
            Leistungsaufnahme — es wird nichts modelliert.
            <b>DIN V 18599-5 Gl. (63)</b> lässt diesen Weg im Bestand ausdrücklich zu
            („Im Bestand darf für P_Pu näherungsweise die zutreffende Leistungsangabe auf dem
            Typschild der Pumpe angenommen werden“). Achten Sie darauf, die tatsächlich
            eingestellte <b>Drehzahlstufe</b> abzulesen — alte Umwälzpumpen geben je Stufe
            eine eigene Wattzahl an.
          </p>

          <h3>Variante „Regelung“ — Trockenläufer</h3>
          <p>
            Die gemessene oder geschätzte elektrische Leistungsaufnahme wird mit der
            Betriebszeit und einem Regelungsfaktor nach
            <b>DIN V 18599-5:2018-09 Tabelle 28</b> multipliziert.
          </p>
          <p class="callout">
            <b>Welche Leistung einzutragen ist.</b> Der Regelungsfaktor beschreibt das
            Verhältnis <em>geregelt zu ungeregelt</em>. Die eingetragene Leistung muss deshalb
            die Aufnahme der <b>ungeregelten</b> Pumpe sein. Wird eine bereits geregelte Pumpe
            gemessen und ihr Messwert eingetragen, wird die Einsparung <b>doppelt</b>
            angerechnet.
          </p>

          <h3>Variante „Effizienzklasse“ — Trockenläufer <em>(veraltet)</em></h3>
          <p class="callout">
            <b>Für neue Maßnahmen gesperrt.</b> Diese Variante rechnet den Motor für
            <em>jede</em> Betriebsstunde mit 100 % der Typenschildleistung. Da Pumpen in der
            Praxis fast immer überdimensioniert sind, liegt das Ergebnis auf derselben Pumpe
            <b>3,2- bis 4,2-mal</b> über der detaillierten Berechnung — die Abweichung ist
            keine Ungenauigkeit, sondern eine fehlende Auslastungsannahme. Als grobe
            <em>Obergrenze</em> brauchbar, als Verbrauchswert nicht. Bestehende Maßnahmen
            rechnen unverändert weiter.
          </p>

          <h3>Variante „DN-Tabelle“ — Nassläufer <em>(veraltet)</em></h3>
          <p class="callout">
            <b>Für neue Maßnahmen gesperrt.</b> Das Verfahren selbst ist in Ordnung, die
            hinterlegten Referenzdaten sind es oberhalb <b>DN 65</b> nicht: dort
            <em>sinkt</em> der tabellierte Verbrauch mit steigender Nennweite
            (DN 80 → DN 100 → DN 125), was physikalisch nicht sein kann. Ursache ist die
            ungleiche Stichprobe einer Produktliste von 2013 — die Zeile DN 125 beruht auf
            drei Pumpen. Die Werte bis DN 65 sind durch Felddaten bestätigt. Für größere
            Nennweiten bitte das <b>Leistungsschild</b> verwenden.
          </p>

          <h4>Effizienzklassen für Antriebe (IE)</h4>
          <table>
            <thead>
              <tr>
                <th>Klasse</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>IE1</td>
                <td>Standard Wirkungsgrad</td>
              </tr>
              <tr>
                <td>IE2</td>
                <td>Hoher Wirkungsgrad</td>
              </tr>
              <tr>
                <td>IE3</td>
                <td>Premium Wirkungsgrad</td>
              </tr>
              <tr>
                <td>IE4</td>
                <td>Super Premium Efficiency</td>
              </tr>
              <tr>
                <td>IE5</td>
                <td>Ultra Premium Efficiency</td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <v-container>
            <h3>Optimierungsmöglichkeiten</h3>
            <p>
              Folgende Maßnahmen haben wesentlichen Einfluss auf die Energiekosten einer Pumpe:
            </p>
            <table>
              <thead>
                <tr>
                  <th>Kategorie</th>
                  <th>Beschreibung</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Motor</td>
                  <td>Austausch der Pumpe durch eine neue energieeffiziente Pumpe</td>
                </tr>
                <tr>
                  <td>Dimensionierung</td>
                  <td>Anpassen der Pumpengröße an den tatsächlichen Bedarf</td>
                </tr>
                <tr>
                  <td>Regelung / Steuerung</td>
                  <td>Einsatz einer stufenlosen Drehzahlregelung (VSD)</td>
                </tr>
                <tr>
                  <td>Betriebszeit</td>
                  <td>Anpassung der Betriebszeit an die tatsächlichen Nutzungszeiten</td>
                </tr>
                <tr>
                  <td>Verbraucher</td>
                  <td>Systemoptimierung zur Reduzierung des Bedarfs</td>
                </tr>
              </tbody>
            </table>

            <p>
              Die Inhalte dieser Hilfe kombinieren die bisherigen Erfahrungswerte aus
              den Legacy-Methoden Trockenläuferpumpe und Nassläuferpumpe.
            </p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

  </div>
</template>

<style scoped>
/* Der Genauigkeitsüberblick nutzt bewusst die normale Tabellendarstellung der übrigen
   Panels — keine eigenen Farben, Badges oder Rahmen. Hier bleibt nur ein Überlaufschutz
   für schmale Fenster. */
.precision {
  overflow-x: auto;
}

/* Hervorgehobener Hinweis für Angaben, deren Missachtung das Ergebnis deutlich verschiebt:
   Referenz-Betriebspunkt bei EEI, der Nutzen-Vorbehalt und die veralteten Varianten. */
.callout {
  margin: 0.75rem 0;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  border-left: 4px solid rgb(var(--v-theme-warning));
  background-color: rgba(var(--v-theme-warning), 0.1);
  line-height: 1.55;
}
</style>